import React from 'react';
import {
  Container, Typography, Stack, Box, Paper, Divider, Link, Breadcrumbs
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../Footer/Footer';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';

const Privacy: React.FC = () => {
  return (

    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs navigation */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', color: 'black' }}
        >
          <HomeIcon sx={{ mr: 0.5, }} fontSize="inherit" />
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/terms"
          sx={{ display: 'flex', alignItems: 'center', color: 'black' }}
        >
          <GavelIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Terms of Service
        </Link>
        <Link
          component={RouterLink}
          to="/privacy"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <PrivacyTipIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Privacy Policy
        </Link>
        <Link
          component={RouterLink}
          to="/guidelines"
          sx={{ display: 'flex', alignItems: 'center', color: 'black' }}
        >
          <PeopleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Community Guidelines
        </Link>
      </Breadcrumbs>

      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, mb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Last Updated: April 17, 2025
          </Typography>
        </Box>
      
        <Typography paragraph>
          At UnitedInTalk, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
          disclose, and safeguard your information when you use our platform.
        </Typography>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            1. Information We Collect
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 500 }}>
            1.1 Personal Information
          </Typography>
          <Typography paragraph>
            When you create an account, we collect information such as your email address and profile information. 
            Name, ethnicity, religion, location, 
            political views, are used to enhance your experience and establish connections with other users.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 500 }}>
            1.2 Usage Data
          </Typography>
          <Typography paragraph>
            For moderation pourposes, we save and monitor chat logs.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 500 }}>
            1.3 Call Data
          </Typography>
          <Typography paragraph>
            To facilitate voice communication between users, we process audio data during calls. This audio is 
            transmitted in real-time and is not recorded or stored by us.
          </Typography>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            2. How We Use Your Information
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>To provide, maintain, and improve our services</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>To match you with other users based on your preferences and profile information</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>To personalize your experience and provide content and features relevant to your interests</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>To communicate with you about updates, security alerts, and support</Typography>
            </Box>
            <Box component="li">
              <Typography>To monitor usage patterns and analyze trends to improve functionality</Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            3. Sharing Your Information
          </Typography>
          <Typography paragraph>
            We do not sell or rent your personal information to third parties. We may share information in the following circumstances:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography><strong>Service Providers:</strong> We use third-party services such as Firebase for authentication.</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography><strong>Legal Requirements:</strong> We may disclose your information only to comply with applicable laws and regulations.</Typography>
            </Box>
            {/*<Box component="li">
              <Typography><strong>Protection of Rights:</strong> We may share information to protect against misuse or unauthorized use of our platform.</Typography>
            </Box>*/}
          </Box>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            4. Data Security
          </Typography>
          <Typography paragraph>
            We implement appropriate security measures to protect your information from unauthorized access 
            and maintain the safety of your personal data. However, please be aware that no method of 
            transmission over the Internet or electronic storage is 100% secure.
          </Typography>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            5. Your Rights
          </Typography>
          <Typography paragraph>
            You have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Access and review the personal information we have collected about you</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Update or correct inaccuracies in your information</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Request deletion of your personal information</Typography>
            </Box>
            <Box component="li">
              <Typography>Opt-out of certain data collection and processing activities</Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            6. Children's Privacy
          </Typography>
          <Typography paragraph>
            Our service is not directed to individuals under the age of 13. We do not knowingly collect 
            personal information from children under 13. If we become aware that we have collected 
            personal information from a child under 13, we will take steps to delete such information.
          </Typography>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            7. Changes to This Privacy Policy
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </Typography>
        </Box>
        
        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            8. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography paragraph>
            Email: team@arczen.com
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle1">
            By using UnitedInTalk, you acknowledge that you have read, understood, and agree to these privacy practices.
          </Typography>
        </Box>
      </Paper>
      
      <Footer/>
    </Container>
  );
}

export default Privacy;